/**
 * Browser input aggregator. Reports keyboard, pointer, gamepad, and
 * configurable virtual axes (used by VirtualJoystick / gamepad sticks).
 *
 * Virtual sources:
 *  - `setVirtualAxis("move", x, y)` overrides keyboard axis for actions whose
 *    name matches the axis (negAction/posAction starts with "move-").
 *  - `setVirtualButton("interact", true)` simulates a key for one or more frames.
 *
 * Call `endFrame()` at the end of each frame to clear just-pressed/just-released
 * sets and decay one-shot virtual buttons.
 */
export class InputManager {
  private keys = new Set<string>();
  private justPressed = new Set<string>();
  private justReleased = new Set<string>();
  private virtualAxes = new Map<string, { x: number; y: number }>();
  private virtualButtons = new Set<string>();
  private gamepadIndex: number | null = null;

  pointerX = 0;
  pointerY = 0;
  pointerDown = false;
  wheelDelta = 0;

  attach(target: HTMLElement | Window = window): () => void {
    const onKeyDown = (e: KeyboardEvent) => {
      const k = e.code;
      if (!this.keys.has(k)) {
        this.justPressed.add(k);
        this.keys.add(k);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      const k = e.code;
      if (this.keys.has(k)) {
        this.justReleased.add(k);
        this.keys.delete(k);
      }
    };
    const onPointerMove = (e: PointerEvent) => {
      this.pointerX = e.clientX;
      this.pointerY = e.clientY;
    };
    const onPointerDown = (e: PointerEvent) => {
      this.pointerDown = true;
      this.pointerX = e.clientX;
      this.pointerY = e.clientY;
    };
    const onPointerUp = () => {
      this.pointerDown = false;
    };
    const onWheel = (e: WheelEvent) => {
      this.wheelDelta += e.deltaY;
    };
    const onBlur = () => {
      this.keys.clear();
      this.pointerDown = false;
    };
    const onGamepadConnected = (e: GamepadEvent) => {
      if (this.gamepadIndex === null) this.gamepadIndex = e.gamepad.index;
    };
    const onGamepadDisconnected = (e: GamepadEvent) => {
      if (this.gamepadIndex === e.gamepad.index) this.gamepadIndex = null;
    };

    target.addEventListener("keydown", onKeyDown as EventListener);
    target.addEventListener("keyup", onKeyUp as EventListener);
    target.addEventListener("pointermove", onPointerMove as EventListener);
    target.addEventListener("pointerdown", onPointerDown as EventListener);
    target.addEventListener("pointerup", onPointerUp as EventListener);
    target.addEventListener("wheel", onWheel as EventListener, { passive: true });
    window.addEventListener("blur", onBlur);
    window.addEventListener("gamepadconnected", onGamepadConnected as EventListener);
    window.addEventListener("gamepaddisconnected", onGamepadDisconnected as EventListener);

    return () => {
      target.removeEventListener("keydown", onKeyDown as EventListener);
      target.removeEventListener("keyup", onKeyUp as EventListener);
      target.removeEventListener("pointermove", onPointerMove as EventListener);
      target.removeEventListener("pointerdown", onPointerDown as EventListener);
      target.removeEventListener("pointerup", onPointerUp as EventListener);
      target.removeEventListener("wheel", onWheel as EventListener);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("gamepadconnected", onGamepadConnected as EventListener);
      window.removeEventListener("gamepaddisconnected", onGamepadDisconnected as EventListener);
    };
  }

  isDown(code: string): boolean {
    return this.keys.has(code) || this.virtualButtons.has(code);
  }

  wasPressed(code: string): boolean {
    return this.justPressed.has(code);
  }

  wasReleased(code: string): boolean {
    return this.justReleased.has(code);
  }

  /** Returns -1, 0, or 1 based on which key is held. */
  axis(neg: string, pos: string): number {
    return (this.isDown(pos) ? 1 : 0) - (this.isDown(neg) ? 1 : 0);
  }

  /**
   * Set a virtual analog axis (-1..1 each). Used by `VirtualJoystick` and
   * gamepad sticks. ActionMap looks these up when present.
   */
  setVirtualAxis(name: string, x: number, y: number): void {
    this.virtualAxes.set(name, { x: clamp(x), y: clamp(y) });
  }

  clearVirtualAxis(name: string): void {
    this.virtualAxes.delete(name);
  }

  getVirtualAxis(name: string): { x: number; y: number } | undefined {
    return this.virtualAxes.get(name);
  }

  /** Press a virtual key for the current frame. */
  pressVirtual(code: string): void {
    if (!this.virtualButtons.has(code)) {
      this.justPressed.add(code);
      this.virtualButtons.add(code);
    }
  }

  releaseVirtual(code: string): void {
    if (this.virtualButtons.has(code)) {
      this.justReleased.add(code);
      this.virtualButtons.delete(code);
    }
  }

  /** Returns the active gamepad (xbox-style) or null. */
  getGamepad(): Gamepad | null {
    if (this.gamepadIndex === null) return null;
    const pads = navigator.getGamepads ? navigator.getGamepads() : [];
    return pads[this.gamepadIndex] ?? null;
  }

  endFrame(): void {
    this.justPressed.clear();
    this.justReleased.clear();
    this.wheelDelta = 0;
  }
}

const clamp = (v: number): number => (v < -1 ? -1 : v > 1 ? 1 : v);
